import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../common/Loader";
import { FaTimes } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import RichTextEditor from "../common/RichTextEditor";

const AddServiceModal = ({ isOpen, onClose, onServiceAdded, allService }) => {
  const [serviceName, setServiceName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(0); // New order field
  const [overview, setOverview] = useState([
    {
      name: "",
      title: "",
      header: "",
      description: "",
      points: [{ heading: "", description: "" }],
      images: [],
    },
  ]);
  const [authorName, setAuthorName] = useState("");
  const [authorDescription, setAuthorDescription] = useState("");
  const [logo, setLogo] = useState("");

  // Upload image to Cloudinary
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

    const res = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
      formData
    );
    return res.data.secure_url;
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    try {
      const uploadPromises = Array.from(files).map((file) =>
        uploadToCloudinary(file)
      );
      const uploadedUrls = await Promise.all(uploadPromises);
      setImages([...images, ...uploadedUrls]);
      toast.success("Images uploaded successfully");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const uploadedUrl = await uploadToCloudinary(file);
      setLogo(uploadedUrl);
      toast.success("Logo uploaded successfully");
    } catch (err) {
      toast.error("Logo upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOverviewImageUpload = async (index, e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    try {
      const uploadPromises = Array.from(files).map((file) =>
        uploadToCloudinary(file)
      );
      const uploadedUrls = await Promise.all(uploadPromises);

      const updatedOverview = [...overview];
      updatedOverview[index].images = [
        ...updatedOverview[index].images,
        ...uploadedUrls,
      ];
      setOverview(updatedOverview);

      toast.success("Overview images uploaded");
    } catch (err) {
      toast.error("Overview image upload failed");
    } finally {
      setLoading(false);
    }
  };

  // --- Overview Functions ---
  const addOverview = () =>
    setOverview([
      ...overview,
      {
        name: "",
        title: "",
        header: "",
        description: "",
        points: [{ heading: "", description: "" }],
        images: [],
      },
    ]);

  const updateOverview = (index, field, value) => {
    const newOverview = [...overview];
    newOverview[index][field] = value;
    setOverview(newOverview);
  };

  const removeOverview = (index) => {
    if (overview.length > 1) {
      const newOverview = overview.filter((_, i) => i !== index);
      setOverview(newOverview);
    }
  };

  // --- Points Functions ---
  const addPoint = (overviewIndex) => {
    const updatedOverview = [...overview];
    updatedOverview[overviewIndex].points.push({
      heading: "",
      description: "",
    });
    setOverview(updatedOverview);
  };

  const updatePoint = (overviewIndex, pointIndex, field, value) => {
    const updatedOverview = [...overview];
    updatedOverview[overviewIndex].points[pointIndex][field] = value;
    setOverview(updatedOverview);
  };

  const removePoint = (overviewIndex, pointIndex) => {
    const updatedOverview = [...overview];
    updatedOverview[overviewIndex].points =
      updatedOverview[overviewIndex].points.filter((_, i) => i !== pointIndex);
    setOverview(updatedOverview);
  };

  // --- Submit Form ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        serviceName,
        description: description
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line),
        title,
        images,
        authorName,
        authorDescription,
        logo,
        order: Number(order), // Include order field
        overview: overview.map((item) => ({
          name: item.name,
          title: item.title || undefined,
          description: item.description
            ? item.description
              .split("\n")
              .map((d) => d.trim())
              .filter((d) => d)
            : undefined,
          points: item.points.filter((p) => p.heading && p.description),
          images: item.images.length > 0 ? item.images : undefined,
        })),
      };

      // Clean payload
      payload.overview = payload.overview.map((item) => {
        const cleanItem = {};
        Object.keys(item).forEach((key) => {
          if (
            item[key] !== undefined &&
            item[key] !== "" &&
            !(Array.isArray(item[key]) && item[key].length === 0)
          ) {
            cleanItem[key] = item[key];
          }
        });
        return cleanItem;
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/services/create-service`,
        payload
      );

      toast.success("Service added successfully");
      onServiceAdded(response?.data?.service);
      setServiceName("");
      setTitle("");
      setDescription("");
      setImages([]);
      setOrder(0); // Reset order field
      setOverview([
        {
          name: "",
          title: "",
          header: "",
          description: "",
          points: [{ heading: "", description: "" }],
          images: [],
        },
      ]);
      setAuthorName("");
      setAuthorDescription("");
      setLogo("");
      onClose();
      if (allService) allService();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add service");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold mb-4">Add New Service</h2>
          <button type="button" onClick={onClose} className="text-gray-600">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Name and Order */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium">Service Name *</label>
              <input
                type="text"
                placeholder="Enter service name"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                required
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          <div>
  <label className="block mb-2 font-medium">Order *</label>
  <input
    type="number"
    placeholder="Enter display order (e.g., 1, 2, 3)"
    value={order}
    onChange={(e) => setOrder(Number(e.target.value))} // ✅ Convert to number
    required
    min="0"
    className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
</div>
          </div>

          {/* Header Title */}
          <div>
            <label className="block mb-2 font-medium">Header Title *</label>
            <input
              type="text"
              placeholder="Enter service heading"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block mb-2 font-medium">Description</label>
            <RichTextEditor 
              theme="snow"
              value={description}
              onChange={setDescription}
              placeholder="Enter service description"
              className="mb-2 font-medium"
            />
          </div>

          {/* Main Images */}
          <div>
            <label className="block mb-2 font-medium">
              Service Images (500×400 size)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="mb-2"
            />
            <div className="flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    alt="service"
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setImages(images.filter((_, index) => index !== i))
                    }
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Overview Sections */}
          <div>
            <h3 className="font-semibold text-lg mb-3">Overview Sections</h3>
            {overview.map((item, i) => (
              <div
                key={i}
                className="border border-gray-300 p-4 rounded-lg mb-4 bg-gray-50"
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-700">
                    Overview Item {i + 1}
                  </h4>
                  {overview.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOverview(i)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {/* Tab Name */}
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Tab Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Frontend Development"
                    value={item.name}
                    onChange={(e) => updateOverview(i, "name", e.target.value)}
                    required
                    className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Header */}
                <div className="mt-4">
                  <label className="block mb-1 text-sm font-medium">
                    Header
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Building Responsive UI"
                    value={item.title}
                    onChange={(e) => updateOverview(i, "title", e.target.value)}
                    className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Description */}
                <div className="mt-4">
                  <label className="block mb-1 text-sm font-medium">Description</label>
                  <RichTextEditor
                    theme="snow"
                    value={item.description}
                    onChange={(val) => updateOverview(i, "description", val)}
                    placeholder="Enter overview description"
                    className="mb-2"
                  />
                </div>

                {/* Points */}
                <div className="mt-4">
                  <label className="block mb-2 text-sm font-medium">
                    Points (Sub Headings)
                  </label>
                  {item.points.map((point, pIndex) => (
                    <div
                      key={pIndex}
                      className="border border-gray-200 p-3 rounded mb-3 bg-white"
                    >
                      <div className="mb-2">
                        <label className="block mb-1 text-xs font-medium text-gray-600">
                          Point Heading
                        </label>
                        <input
                          type="text"
                          placeholder="Point Heading"
                          value={point.heading}
                          onChange={(e) =>
                            updatePoint(i, pIndex, "heading", e.target.value)
                          }
                          className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block mb-1 text-xs font-medium text-gray-600">
                          Point Description
                        </label>
                        <RichTextEditor
                          value={point.description}
                          onChange={(val) => updatePoint(i, pIndex, "description", val)}
                          placeholder="Enter point description..."
                        />
                      </div>
                      {item.points.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePoint(i, pIndex)}
                          className="text-red-500 text-sm hover:text-red-700"
                        >
                          Remove Point
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addPoint(i)}
                    className="bg-green-500 text-white px-3 py-1 rounded text-sm mt-2 hover:bg-green-600"
                  >
                    + Add Point
                  </button>
                </div>

                {/* Overview Images */}
                <div className="mt-4">
                  <label className="block mb-1 text-sm font-medium">
                    Images (500×400 size)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleOverviewImageUpload(i, e)}
                    className="mb-2"
                  />
                  <div className="flex flex-wrap gap-2">
                    {item.images.map((img, j) => (
                      <div key={j} className="relative">
                        <img
                          src={img}
                          alt="overview"
                          className="w-16 h-16 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updatedOverview = [...overview];
                            updatedOverview[i].images =
                              updatedOverview[i].images.filter(
                                (_, index) => index !== j
                              );
                            setOverview(updatedOverview);
                          }}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addOverview}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              + Add Overview Section
            </button>
          </div>

          {/* Author Info */}
          <div>
            <label className="block mb-2 font-medium">Author Name</label>
            <input
              type="text"
              placeholder="Enter author name"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block mb-2 font-medium">Author Description</label>
            <RichTextEditor
              theme="snow"
              value={authorDescription}
              onChange={setAuthorDescription}
              placeholder="Enter author description"
              className="mb-2"
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block mb-2 font-medium">
              Logo (160×160 size)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="mb-2"
            />
            {logo && (
              <div className="mt-2">
                <img
                  src={logo}
                  alt="logo"
                  className="w-20 h-20 object-cover rounded border"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded disabled:opacity-50"
            >
              {loading ? <Loader /> : "Add Service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddServiceModal;